import React from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, Microscope, ChartLineUp, Heartbeat, Pulse, Waveform, Drop, CaretRight, EnvelopeSimple } from "@phosphor-icons/react";
import SEO from "../components/SEO";
import SectionHeading from "../components/SectionHeading";

const CAPABILITIES = [
  { icon: Heartbeat, title: "Ambulatory blood pressure monitoring (24-hr / 48-hr)", body: "British Hypertension Society-validated devices. Investigator-blinded workflows and central-reader-ready outputs available." },
  { icon: ChartLineUp, title: "Central & peripheral haemodynamics", body: "Aortic pulse wave velocity, augmentation index and central blood pressure — commonly used as pharmacodynamic endpoints in cardiovascular studies." },
  { icon: Pulse, title: "12-lead ECG", body: "Resting 12-lead ECGs acquired at protocol-defined visits, with paper and digital transfer options. Anonymised, protocol-labelled outputs delivered ready for central over-read." },
  { icon: Pulse, title: "Ambulatory Holter & event monitoring", body: "24-hr, 48-hr and 7-day Holter recordings, plus patient-triggered cardiac event monitors up to 14 days. Full-disclosure recordings and consultant-reported summaries in study-specific formats." },
  { icon: ShieldCheck, title: "GCP-aligned procedures", body: "All measurements performed to ICH E6 (R3) Good Clinical Practice standards, with study-specific SOPs, source data timestamps and audit-ready records." },
];

const SIGNATURE = [
  {
    icon: Waveform,
    title: "Flow-mediated dilatation (FMD)",
    body: "High-resolution brachial artery ultrasound with automated edge-detection quantifies endothelium-dependent, NO-mediated vasodilatation — a well-established pharmacodynamic endpoint in early-phase cardiovascular trials.",
    media: {
      type: "image",
      src: "https://customer-assets-v7afamib.emergentagent.net/job_clinical-lab-hub-2/artifacts/2xokvqcv_image.png",
      alt: "Participant's arm supported on a foam wedge with a red upper-arm cuff and a stereotactic-mounted linear ultrasound probe imaging the brachial artery during a flow-mediated dilatation measurement.",
    },
  },
  {
    icon: Drop,
    title: "Forearm blood flow (FBF)",
    body: "Venous-occlusion strain-gauge plethysmography with fine intra-arterial cannulation — the reference technique for regional vascular pharmacology of endothelium-dependent and -independent vasoactive agents.",
    media: {
      type: "image",
      src: "https://customer-assets-v7afamib.emergentagent.net/job_clinical-lab-hub-2/artifacts/n1jntqx6_image.png",
      alt: "Supine participant with both forearms outstretched on padded supports, showing bilateral wrist BP cuffs, upper-arm occlusion cuffs and mercury-in-silastic strain gauges used for venous-occlusion forearm plethysmography.",
    },
  },
  {
    icon: Microscope,
    title: "Echocardiography — EACVI-standard",
    body: "Transthoracic 2D and 3D echocardiography performed and reported to EACVI-accredited standards. DICOM acquisition, storage and transfer to sponsor or independent imaging core laboratories.",
    media: {
      type: "video",
      src: "/videos/echo_clip.mp4",
      poster: "/videos/echo_poster.png",
      alt: "Live research echocardiography clip acquired at Cambridge showing a transthoracic echo view of the heart.",
    },
  },
];

const PHASES = [
  { p: "Phase I", desc: "Deep cardiovascular phenotyping for first-in-human safety and PK/PD studies — ABPM, ECG, echo and central haemodynamics at protocol-defined visits." },
  { p: "Phase II", desc: "Pharmacodynamic endpoints for proof-of-concept and dose-finding studies in hypertension, hypercholesterolaemia, heart failure and cardiometabolic disease." },
  { p: "Phase III", desc: "Cardiovascular sub-study support and safety endpoints for pivotal trials — reliable, standardised measurements across visits." },
];

const ACCRED = [
  { name: "ICH E6 (R3) — Good Clinical Practice", desc: "All measurement work is delivered to the current ICH E6 (R3) revision of Good Clinical Practice — the international quality standard for the design, conduct and recording of clinical trial data." },
  { name: "EACVI accreditation — cardiovascular imaging", desc: "Echocardiographic studies are performed and reported to European Association of Cardiovascular Imaging accredited standards, meeting the requirements of sponsored trials and imaging core labs." },
];

export default function Research() {
  return (
    <div data-testid="research-page">
      <SEO
        title="Research Services for Cardiovascular Clinical Trials"
        description="Cardiovascular measurement services for CROs and MHRA-accredited Phase 1 units. ICH E6 (R3) GCP-aligned procedures and EACVI-standard cardiovascular imaging."
        path="/research"
      />

      {/* Hero */}
      <section className="bg-cream-100 border-b border-bronze-light/30">
        <div className="container-ccc py-20 lg:py-28 grid lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-7">
            <div className="eyebrow">Research services</div>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl mt-4 leading-[1.05]">
              Cardiovascular measurements for CROs and clinical trials units.
            </h1>
            <p className="mt-6 text-ink-muted text-lg leading-relaxed max-w-2xl">
              We provide the cardiovascular assessments and diagnostics your protocol needs — blood pressure, ECG, echocardiography, Holter, arterial haemodynamics and cardiometabolic profiling — delivered to <strong className="text-ink">ICH E6 (R3) Good Clinical Practice</strong> and <strong className="text-ink">EACVI</strong> imaging standards. We are the specialist cardiovascular arm that supports CROs, sponsors and MHRA-accredited Phase 1 units with reliable, protocol-aligned data.
            </p>
            <p className="mt-4 text-ink-muted text-lg leading-relaxed max-w-2xl">
              Since our inception in <strong className="text-ink">2016</strong>, we have delivered cardiovascular endpoints for studies with <strong className="text-ink">leading pharmaceutical sponsors</strong> across first-in-human and early-phase programmes.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <a
                href="mailto:enquiries@camcpc.co.uk?subject=Sponsor%20enquiry"
                data-testid="research-sponsor-cta"
                className="btn-primary inline-flex items-center gap-2"
              >
                <EnvelopeSimple size={16} /> Sponsor or CRO enquiry
              </a>
              <Link to="/consultants" className="btn-secondary">Meet the team</Link>
            </div>
          </div>
          <div className="lg:col-span-5 space-y-3">
            {ACCRED.map((a) => (
              <div key={a.name} className="p-5 bg-white border border-bronze-light/40 rounded-md">
                <div className="flex items-start gap-3">
                  <ShieldCheck size={22} weight="duotone" className="text-bronze-dark shrink-0 mt-0.5" />
                  <div>
                    <div className="font-serif text-lg leading-tight">{a.name}</div>
                    <p className="mt-1.5 text-sm text-ink-muted leading-relaxed">{a.desc}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Capabilities */}
      <section className="container-ccc py-20">
        <SectionHeading eyebrow="What we deliver" title="The cardiovascular endpoints your protocol needs." />
        <p className="mt-6 max-w-3xl text-ink-muted leading-relaxed">
          We provide the same clinical assessments we offer patients, but performed and documented to trial standard. Data is delivered anonymised and protocol-labelled, ready to hand off to your CRO, sponsor or imaging core lab.
        </p>

        {/* Row 1 — standard capabilities (2-col, icon-only) */}
        <div className="mt-12 grid md:grid-cols-2 gap-6">
          {CAPABILITIES.slice(0, 2).map(({ icon: Icon, title, body }) => (
            <div key={title} className="p-6 bg-white border border-stone-200/60 rounded-md">
              <Icon size={26} weight="duotone" className="text-bronze-dark" />
              <h3 className="mt-3 font-serif text-xl leading-tight">{title}</h3>
              <p className="mt-2.5 text-sm text-ink-muted leading-relaxed">{body}</p>
            </div>
          ))}
        </div>

        {/* Row 2 — signature techniques (3-col, with photos / live clip) */}
        <div className="mt-6 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {SIGNATURE.map(({ icon: Icon, title, body, media }) => (
            <div key={title} className="bg-white border border-bronze-light/40 rounded-md overflow-hidden flex flex-col">
              <div className="aspect-[16/10] w-full overflow-hidden bg-black">
                {media.type === "video" ? (
                  <video
                    src={media.src}
                    poster={media.poster}
                    aria-label={media.alt}
                    autoPlay
                    loop
                    muted
                    playsInline
                    preload="metadata"
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <img
                    src={media.src}
                    alt={media.alt}
                    loading="lazy"
                    className="w-full h-full object-cover"
                  />
                )}
              </div>
              <div className="p-6 flex-1">
                <div className="flex items-center gap-2.5">
                  <Icon size={22} weight="duotone" className="text-bronze-dark" />
                  <span className="text-[10px] uppercase tracking-[0.18em] font-medium text-bronze-dark/80">Signature technique</span>
                </div>
                <h3 className="mt-2 font-serif text-xl leading-tight">{title}</h3>
                <p className="mt-3 text-sm text-ink-muted leading-relaxed">{body}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Row 3 — remaining capabilities (3-col, icon-only) */}
        <div className="mt-6 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {CAPABILITIES.slice(2).map(({ icon: Icon, title, body }) => (
            <div key={title} className="p-6 bg-white border border-stone-200/60 rounded-md">
              <Icon size={26} weight="duotone" className="text-bronze-dark" />
              <h3 className="mt-3 font-serif text-xl leading-tight">{title}</h3>
              <p className="mt-2.5 text-sm text-ink-muted leading-relaxed">{body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Phase support */}
      <section className="bg-cream-100 border-y border-bronze-light/30">
        <div className="container-ccc py-20 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-4">
            <div className="eyebrow">Study phases we support</div>
            <h2 className="font-serif text-3xl lg:text-4xl mt-4 leading-tight">Cardiovascular data across every trial phase.</h2>
            <p className="mt-5 text-ink-muted leading-relaxed">
              Since 2016 we have delivered cardiovascular endpoints for studies with leading pharmaceutical sponsors and their CROs, spanning first-in-human dose-escalation through to late-phase confirmatory work in MHRA-accredited Phase 1 clinical trials units.
            </p>
          </div>
          <div className="lg:col-span-8 space-y-4">
            {PHASES.map((p) => (
              <div key={p.p} className="p-6 bg-white border-l-2 border-bronze-dark rounded-md">
                <div className="font-serif text-xl">{p.p}</div>
                <p className="mt-2 text-sm text-ink-muted leading-relaxed">{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Investigators */}
      <section className="container-ccc py-20">
        <SectionHeading eyebrow="The team" title="Two internationally-recognised cardiovascular researchers." />
        <div className="mt-10 grid md:grid-cols-2 gap-8">
          <div className="p-6 bg-white border border-stone-200/60 rounded-md">
            <div className="font-serif text-2xl">Professor Ian Wilkinson</div>
            <div className="text-sm text-ink-muted mt-1">Professor of Therapeutics, University of Cambridge · Immediate Past-President, British and Irish Hypertension Society</div>
            <p className="mt-3 text-sm text-ink-muted leading-relaxed">
              Internationally recognised research profile in hypertension. High-impact publications in The Lancet, JAMA, Circulation, European Heart Journal and Hypertension. Co-author of UK and European hypertension guidelines.
            </p>
            <Link to="/consultants/prof-ian-wilkinson" className="mt-4 inline-flex items-center gap-1.5 text-sm text-bronze-dark hover:text-bronze">Read profile <CaretRight size={14} /></Link>
          </div>
          <div className="p-6 bg-white border border-stone-200/60 rounded-md">
            <div className="font-serif text-2xl">Dr Joseph Cheriyan</div>
            <div className="text-sm text-ink-muted mt-1">Affiliated Associate Professor, University of Cambridge · Director, Cardiovascular Trials Office, Cambridge Clinical Trials Unit</div>
            <p className="mt-3 text-sm text-ink-muted leading-relaxed">
              <strong className="text-ink">Extensive industry experience delivering cardiovascular measurements within an MHRA-accredited Phase 1 clinical trials unit</strong>, working with sponsors and CROs across a wide range of first-in-human and early-phase studies. High-impact publications in Nature Medicine, Circulation and Hypertension.
            </p>
            <Link to="/consultants/dr-joseph-cheriyan" className="mt-4 inline-flex items-center gap-1.5 text-sm text-bronze-dark hover:text-bronze">Read profile <CaretRight size={14} /></Link>
          </div>
        </div>
      </section>

      {/* Sponsor CTA */}
      <section className="bg-ink text-cream-50">
        <div className="container-ccc py-20 text-center">
          <div className="eyebrow text-bronze-light">Sponsor & CRO enquiries</div>
          <h2 className="font-serif text-3xl lg:text-4xl mt-4 max-w-3xl mx-auto leading-tight">
            Have a protocol needing cardiovascular measurements? We&apos;d be glad to discuss.
          </h2>
          <p className="mt-5 text-cream-50/80 max-w-2xl mx-auto leading-relaxed">
            Send a brief outline of the endpoints, phase and indication and we&apos;ll come back to you with feasibility, timelines and per-visit measurement pricing.
          </p>
          <a
            href="mailto:enquiries@camcpc.co.uk?subject=Sponsor%20enquiry"
            data-testid="research-footer-cta"
            className="btn-primary mt-8 inline-flex items-center gap-2"
          >
            <EnvelopeSimple size={16} /> enquiries@camcpc.co.uk
          </a>
        </div>
      </section>
    </div>
  );
}
